import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { toast } from "react-toastify";

import Button from "components/button/Button";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import TemplateViewImage from "components/icons/TemplateViewImage";
import layoutStyle from "components/layout/layout.scss";
import Modal from "components/modal/Modal";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import {
    ActiveWorkflowDialog,
    ConflictsResponse,
    RequestFailure,
    TemplateDetailsDialog,
    toConflictsResponse,
} from "components/workflows/workflow-templates/PublishedWorkflowTemplate";
import WorkflowTemplateDetailsDialog from "components/workflows/workflow-templates/WorkflowTemplateDetailsDialog";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { BdeOnboardingSteps, BmdeOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { TemplateTableData } from "domain/workflowTemplates";
import { FLAG_WORKFLOW_EDITOR_V3 } from "services/feature/FeatureFlagService";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { ConflictBehavior, templateService, WorkflowTemplateSaveResponse } from "services/workflows/TemplateService";
import {
    ManifestWorkflowEditor,
    Profile,
    WorkflowEditorV3SupportedEntry,
    workflowService,
} from "services/workflows/WorkflowService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepCompleted?: boolean;
    showWorflowTemplateView: (value: boolean) => void;
    setHideSecondStep?: (value: boolean) => void;
    defaultWorkflowTemplate: TemplateTableData;
    requestFailureMessage?: string;
    product: OnboardingProductType;
    stepName: string;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
}

const WorkflowTemplateSubStep = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const workflowEditorV3Flag = useFeature(FLAG_WORKFLOW_EDITOR_V3);
    const abortController = new AbortController();
    const [loading, setLoading] = React.useState(false);
    const [showError, setShowError] = React.useState(false);
    const completeStep = () => {
        saveOnboardingStep();
    };
    const [templateDetailsDialog, setTemplateDetailsDialog] = React.useState<TemplateDetailsDialog>({
        show: false,
    });
    const theme = useTheme();
    const [addedBadge, setAddedBadge] = React.useState<boolean>(false);
    const [updatePriority, setUpdatePriority] = React.useState<boolean>(true);
    const [requestFailure, setRequestFailure] = React.useState<RequestFailure | null>(null);
    const [conflictsException, setConflictsException] = React.useState<ConflictsResponse | undefined>(undefined);
    const [showActiveWorkflowDialog, setShowActiveWorkflowDialog] = React.useState<ActiveWorkflowDialog>({
        show: false,
        workflowUuid: "",
    });
    const [activeWorkflowLoading, setActiveWorkflowLoading] = React.useState<boolean>(false);
    const [workflowEditors, setWorkflowEditors] = React.useState<ManifestWorkflowEditor[]>([]);
    const [workflowEditorV3Supported, setWorkflowEditorV3Supported] = React.useState<WorkflowEditorV3SupportedEntry[]>(
        []
    );
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: props.product,
            step:
                props.product === OnboardingProductType.BMDE
                    ? BmdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE
                    : BdeOnboardingSteps.STEP_WORKFLOW_TEMPLATE,
            workflowTemplateUuid: props.defaultWorkflowTemplate.uuid,
        });
    };

    React.useEffect(() => {
        if (props.defaultWorkflowTemplate.name === "") {
            setAndShowErrorModal();
        }

        const abortController = new AbortController();
        abortControllers.push(abortController);
        workflowService.fetchWorkflowEditors(Profile.ALL, abortController).then((workflowEditors) => {
            setWorkflowEditors(workflowEditors);
        });

        if (workflowEditorV3Flag) {
            const v3Controller = new AbortController();
            abortControllers.push(v3Controller);
            workflowService
                .fetchWorkflowEditorV3Supported(v3Controller)
                .then(setWorkflowEditorV3Supported)
                .catch(() =>
                    logger.warn("Failed to fetch V3 supported versions; V3 editor options will not be shown.")
                );
        }

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const addTemplateToTenantsWorkflows = (fromPreview: boolean) => {
        setLoading(true);
        templateService
            .save(props.defaultWorkflowTemplate.uuid, ConflictBehavior.DEFAULT)
            .then((response: WorkflowTemplateSaveResponse) => {
                setRequestFailure(null);
                setAddedBadge(true);
                setUpdatePriority(false);
                markWorkflowAsActive(response.workflowUuid);
            })
            .catch((exception) => {
                try {
                    if (!abortController.signal.aborted) {
                        if (fromPreview) {
                            setConflictsException(toConflictsResponse(JSON.parse(exception.message)));
                            setRequestFailure({
                                message: t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.message"),
                                title: t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.title"),
                            });
                            setAddedBadge(false);
                            setUpdatePriority(true);
                            setShowActiveWorkflowDialog({
                                show: false,
                                workflowUuid: "",
                            });
                        } else {
                            fetchCorrespondingWorkflow();
                            setRequestFailure(null);
                        }
                    }
                } catch {
                    setConflictsException(undefined);
                    setAddedBadge(false);
                    setUpdatePriority(true);
                    setShowError(true);
                }
            });
    };

    const hideErrorModal = () => {
        setShowError(false);
    };

    const showSuccessNotification = (active: boolean, isBmpTemplate: boolean) => {
        let message = t("workflowTemplate.workflowTemplateDetailsDialog.successToast.success", {
            templateName: templateDetailsDialog.details?.name,
        });
        if (active) {
            message +=
                " " +
                (isBmpTemplate
                    ? t("workflowTemplate.workflowTemplateDetailsDialog.successToast.active")
                    : t("workflowTemplate.workflowTemplateDetailsDialog.successToast.default"));
        }
        return toast(
            <SuccessIcon
                successClass={layoutStyle.customToastSuccessIcon}
                color={theme.contentBackgroundColor}
                text={message}
            />,
            {
                closeButton: (closeToastProps) => (
                    <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                ),
                className: layoutStyle.customToastSuccessMessage,
            }
        );
    };

    const markWorkflowAsActive = (workflowUuid: string) => {
        workflowService
            .setDefault(workflowUuid, abortController)
            .then(() => {
                setLoading(false);
                completeStep();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setShowError(true);
                }
                setShowError(true);
            });
        setActiveWorkflowLoading(false);
    };

    const fetchCorrespondingWorkflow = () => {
        workflowService
            .fetchWorkflows(props.defaultWorkflowTemplate.name, "", "", "", "", "", "", abortController)
            .then((data) => {
                markWorkflowAsActive(data.workflowTableData[0].uuid);
            })
            .catch(() => {
                setShowError(true);
            });
    };

    const setAndShowErrorModal = () => {
        setRequestFailure({
            message: t("workflowTemplatesManager.template.requestFailed"),
            title: t("workflowTemplate.workflowTemplateDetailsDialog.error.generic.title"),
        });
        setShowError(true);
    };

    const deduceProductName = () => {
        if (props.product === OnboardingProductType.BMDE) {
            return "Blancco Mobile Diagnostics and Erasure";
        }
        if (props.product === OnboardingProductType.BMWE) {
            return "Blancco Mobile Workspace Edition";
        }
        return "Blancco Drive Eraser";
    };

    return (
        <>
            <div className={style.inProgressInfoBox}>
                <div className={style.inProgressDescription}>
                    {t("Onboarding.gettingStartedFlow.workflowStep.description1", { productName: deduceProductName() })}
                </div>
                <div className={style.inProgressDescription}>
                    {t("Onboarding.gettingStartedFlow.workflowStep.description2")}
                </div>
            </div>
            <div className={style.previewTemplateInfoText}>
                {t("Onboarding.gettingStartedFlow.workflowStep.previewTemplateInfoText")}
                <div className={style.previewTemplateBox}>
                    <TemplateViewImage />
                    <div className={style.previewTemplateBlock}>
                        <div className={style.previewTemplateHeading}>
                            {props.defaultWorkflowTemplate.name === ""
                                ? props.product
                                : props.defaultWorkflowTemplate.name}
                        </div>
                        <div>
                            {props.product === OnboardingProductType.BDE
                                ? t("Onboarding.gettingStartedFlow.workflowStep.templateDescription")
                                : t("Onboarding.bmde.gettingStarted.steps.chooseWorkflow.templateDescription")}
                        </div>
                        <div>
                            <button
                                className={style.previewTemplateButton}
                                onClick={() => {
                                    setTemplateDetailsDialog({
                                        show: true,
                                        details: props.defaultWorkflowTemplate,
                                    });
                                    setRequestFailure(null);
                                }}
                            >
                                {t("Onboarding.gettingStartedFlow.workflowStep.previewTemplate.button")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={style.templateSelectButtonLayout}>
                {props.stepCompleted ? (
                    <StepCompletedText
                        successText={t(
                            "Onboarding.gettingStartedFlow.workflowStep.previewTemplate.defaultTemplateSelected"
                        )}
                        testId={testIds.workArea.onboarding.step.statusLabel}
                        dataType={props.product + "_" + props.stepName}
                    />
                ) : (
                    <Button
                        variant={"PRIMARY"}
                        className={classNames(style.templateSelectButton)}
                        onClick={() => {
                            props.saveStepInProgressIndex(0);
                            addTemplateToTenantsWorkflows(false);
                        }}
                        testId={testIds.workArea.onboarding.step.primaryButton}
                        data-type={props.product + "_" + props.stepName}
                        loadingAnimation={props.stepInProgressIndex === 0}
                        disabled={props.stepInProgressIndex === 0}
                    >
                        {props.stepInProgressIndex === 0
                            ? t("Onboarding.saving")
                            : t("Onboarding.gettingStartedFlow.workflowStep.markComplete")}
                    </Button>
                )}
            </div>
            <Modal
                isOpen={showError}
                hideModal={hideErrorModal}
                modalTitle={
                    requestFailure?.title ? requestFailure.title : t("Onboarding.bmde.errorMessages.errorOccurred")
                }
            >
                <>
                    <div className={style.errorMessageContainer}>
                        {t("Onboarding.bmde.errorMessages.fetchingTemplatesFailed", {
                            email: SUPPORT_EMAIL,
                        })}
                    </div>
                    <div className={style.errorButtonContainer}>
                        <button
                            className={classNames(
                                buttons.primaryButton,
                                buttons.medium,
                                buttons.okButton,
                                buttons.buttonWithoutIcon
                            )}
                            onClick={() => {
                                setShowError(false);
                            }}
                        >
                            {t("Common.ok")}
                        </button>
                    </div>
                </>
            </Modal>
            <WorkflowTemplateDetailsDialog
                showTemplateDetailsDialog={templateDetailsDialog}
                setShowTemplateDetailsDialog={setTemplateDetailsDialog}
                setAddedBadge={setAddedBadge}
                requestFailure={requestFailure}
                loading={loading}
                conflictsException={conflictsException}
                addedBadge={addedBadge}
                saveTemplate={() => {
                    addTemplateToTenantsWorkflows(true);
                }}
                showActiveWorkflowDialog={showActiveWorkflowDialog}
                setShowActiveWorkflowDialog={setShowActiveWorkflowDialog}
                setSavedWorkflowActive={() => {
                    markWorkflowAsActive(showActiveWorkflowDialog.workflowUuid);
                }}
                showSuccessNotificationToast={showSuccessNotification}
                activeWorkflowLoading={activeWorkflowLoading}
                workflowEditors={workflowEditors}
                workflowEditorV3Supported={workflowEditorV3Supported}
                previewOnly={true}
                updatePriority={updatePriority}
            />
        </>
    );
};

export default connector(WorkflowTemplateSubStep);
